import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, RefreshCw, Search } from 'lucide-react';
import api from '../api';
import ErrorAlert from '../components/ErrorAlert';

const BUCKET_LABELS = {
  creative: 'Creative',
  flavour: 'Flavour',
  nature: 'Nature',
  activities: 'Activities',
  shops: 'Shops',
  practical: 'Practical',
  special_occasion: 'Special Occasion',
  needs_classification: 'Needs Classification',
  unmapped: 'Unmapped',
  local_only: 'Local Only',
};

const STATUS_LABELS = {
  first_party_source: 'First-party source',
  pointer_directory_source: 'Pointer/directory',
  no_source: 'No source',
  scrape_failed: 'Scrape failed',
  enriched: 'Enriched',
  failed: 'Failed',
  never_run: 'Never run',
  stale: 'Stale',
  composer_ready: 'Composer-ready',
  approved_but_unpublished: 'Approved, unpublished',
  published_projection_stale: 'Published projection stale',
  pending_change_request: 'Pending change request',
  pending_attribute_review: 'Pending attribute review',
  not_enriched: 'Not enriched',
  enrichment_failed: 'Enrichment failed',
  never_enriched: 'Never enriched',
  not_published: 'Not published',
  untrusted_coordinates: 'Untrusted coordinates',
  source_quality: 'Source quality',
  source_blocker: 'Source blocker',
  needs_classification: 'Needs classification',
  anchor_activity: 'Anchor activity',
  slot_activity: 'Slot activity',
  nature_residue: 'Nature residue',
  special_occasion: 'Special occasion',
};

function label(value) {
  if (value === null || value === undefined || value === '') return '-';
  const text = String(value);
  return STATUS_LABELS[text] || text.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function badgeClass(value) {
  if (value === true || value === 'composer_ready' || value === 'published' || value === 'trusted' || value === 'first_party_source' || value === 'enriched') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (['not_published', 'never_run', 'never_enriched', 'unmapped', 'needs_classification', 'stale', 'pending_attribute_review', 'pending_change_request'].includes(value)) {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  if (['no_source', 'source_quality', 'source_blocker', 'scrape_failed', 'failed', 'enrichment_failed', 'pointer_directory_source', 'published_projection_stale', 'untrusted_coordinates', 'missing'].includes(value)) {
    return 'border-red-200 bg-red-50 text-red-700';
  }
  return 'border-stone-200 bg-stone-50 text-stone-700';
}

function Badge({ value }) {
  if (!value) return null;
  return (
    <span className={`inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${badgeClass(value)}`}>
      {label(value)}
    </span>
  );
}

function workbenchHref(row) {
  return row.workbench_url || `#places?place_id=${encodeURIComponent(row.place_master_id)}&card_key=${encodeURIComponent(row.card_key || 'primary')}`;
}

function SummaryCard({ title, value, sub }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-stone-500">{title}</p>
      <p className="mt-1 text-2xl font-semibold text-stone-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-stone-500">{sub}</p>}
    </div>
  );
}

function CountChips({ counts = {} }) {
  const entries = Object.entries(counts).filter(([, count]) => Number(count) > 0);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([key, count]) => (
        <span key={key} className="rounded-full border border-stone-200 bg-white px-2 py-0.5 text-[11px] text-stone-600">
          {label(key)} {count}
        </span>
      ))}
    </div>
  );
}

function Row({ row }) {
  const buckets = Array.isArray(row.launch_buckets) ? row.launch_buckets : [row.launch_bucket].filter(Boolean);
  return (
    <tr className="border-t border-stone-100 align-top hover:bg-stone-50">
      <td className="px-3 py-2">
        <a
          href={workbenchHref(row)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex max-w-[260px] items-center gap-1 font-medium text-blue-700 hover:text-blue-900"
        >
          <span className="truncate">{row.name}</span>
          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
        </a>
        <p className="mt-0.5 text-[11px] text-stone-500">{row.place_master_id} · {row.card_key || 'primary'}</p>
        {row.address && <p className="mt-0.5 max-w-[260px] truncate text-[11px] text-stone-400">{row.address}</p>}
      </td>
      <td className="px-3 py-2 text-xs text-stone-600">
        <p>{label(row.category_type || row.card_category)}</p>
        <p className="text-stone-400">{label(row.sector_primary || row.card_sector)}</p>
        <div className="mt-1 flex max-w-[220px] flex-wrap gap-1">
          {buckets.map(bucket => <Badge key={bucket} value={bucket} />)}
          {row.activity_duration_flag && <Badge value={row.activity_duration_flag} />}
          {row.rental_provider_flag && <Badge value={row.rental_provider_flag} />}
          {row.multi_offering_flag && <Badge value={row.multi_offering_flag} />}
        </div>
      </td>
      <td className="px-3 py-2"><Badge value={row.source_status} /></td>
      <td className="px-3 py-2"><Badge value={row.enrichment_status} /></td>
      <td className="px-3 py-2"><Badge value={row.publish_status} /></td>
      <td className="px-3 py-2"><Badge value={row.coordinate_trust} /></td>
      <td className="px-3 py-2"><Badge value={row.composer_ready ? 'composer_ready' : row.composer_blocker} /></td>
    </tr>
  );
}

function DuplicatePair({ pair }) {
  const left = pair.left || {};
  const right = pair.right || {};
  return (
    <tr className="border-t border-stone-100 align-top hover:bg-stone-50">
      {[left, right].map((card, index) => (
        <td key={index} className="px-3 py-2">
          <a
            href={card.workbench_url || `#places?place_id=${encodeURIComponent(card.place_master_id)}&card_key=${encodeURIComponent(card.card_key || 'primary')}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex max-w-[260px] items-center gap-1 font-medium text-blue-700 hover:text-blue-900"
          >
            <span className="truncate">{card.name}</span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          </a>
          <p className="mt-0.5 text-[11px] text-stone-500">{card.place_master_id} · {card.card_key || 'primary'}</p>
          {card.address && <p className="mt-0.5 max-w-[260px] truncate text-[11px] text-stone-400">{card.address}</p>}
        </td>
      ))}
      <td className="px-3 py-2 text-xs text-stone-600">
        <p>Name {Math.round((pair.name_similarity || 0) * 100)}%</p>
        <p>{pair.distance_meters === null || pair.distance_meters === undefined ? 'Distance unknown' : `${pair.distance_meters}m apart`}</p>
        <div className="mt-1 flex flex-wrap gap-1">
          {(pair.reasons || []).map(reason => <Badge key={reason} value={reason} />)}
        </div>
      </td>
    </tr>
  );
}

function DuplicateReview({ pairs }) {
  if (!pairs?.length) return null;
  return (
    <section className="overflow-hidden rounded-lg border border-stone-200 bg-white">
      <div className="border-b border-stone-200 bg-stone-50 px-4 py-3">
        <h2 className="font-semibold text-stone-900">Duplicate Review Candidates</h2>
        <p className="text-xs text-stone-500">{pairs.length} possible duplicate pair{pairs.length === 1 ? '' : 's'} · review only, no automatic merge</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-white text-[11px] font-medium uppercase tracking-wide text-stone-400">
            <tr>
              <th className="px-3 py-2">Card A</th>
              <th className="px-3 py-2">Card B</th>
              <th className="px-3 py-2">Why flagged</th>
            </tr>
          </thead>
          <tbody>{pairs.map((pair, index) => <DuplicatePair key={`${pair.left?.place_master_id}:${pair.right?.place_master_id}:${index}`} pair={pair} />)}</tbody>
        </table>
      </div>
    </section>
  );
}

function PublishingRow({ row }) {
  const buckets = Array.isArray(row.launch_buckets) ? row.launch_buckets : [row.launch_bucket].filter(Boolean);
  return (
    <tr className="border-t border-stone-100 align-top hover:bg-stone-50">
      <td className="px-3 py-2">
        <a
          href={workbenchHref(row)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex max-w-[280px] items-center gap-1 font-medium text-blue-700 hover:text-blue-900"
        >
          <span className="truncate">{row.name}</span>
          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
        </a>
        <p className="mt-0.5 text-[11px] text-stone-500">{row.place_master_id} · {row.card_key || 'primary'}</p>
      </td>
      <td className="px-3 py-2">
        <Badge value={row.priority_gap} />
        <div className="mt-1 flex max-w-[220px] flex-wrap gap-1">
          {buckets.map(bucket => <Badge key={bucket} value={bucket} />)}
        </div>
      </td>
      <td className="px-3 py-2"><Badge value={row.publishing_blocker} /></td>
      <td className="px-3 py-2 text-xs text-stone-600">
        <p>{label(row.category_type || row.card_category)}</p>
        <p className="text-stone-400">{label(row.sector_primary || row.card_sector)}</p>
      </td>
      <td className="px-3 py-2 space-y-1">
        <Badge value={row.source_status} />
        <Badge value={row.enrichment_status} />
      </td>
      <td className="px-3 py-2 space-y-1">
        <Badge value={row.admin_review_status || row.review_status} />
        <Badge value={row.publish_status || 'not_published'} />
      </td>
      <td className="px-3 py-2"><Badge value={row.coordinate_trust} /></td>
    </tr>
  );
}

function PublishingWorklist({ worklist }) {
  if (!worklist) return null;
  return (
    <section className="overflow-hidden rounded-lg border border-stone-200 bg-white">
      <div className="border-b border-stone-200 bg-stone-50 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold text-stone-900">Publishing Worklist</h2>
            <p className="text-xs text-stone-500">
              {worklist.total || 0} guest-relevant Bracebridge card{worklist.total === 1 ? '' : 's'} not published · grouped by primary blocker
            </p>
          </div>
          <CountChips counts={worklist.priority_gap_counts} />
        </div>
      </div>
      <div className="space-y-3 p-4">
        {(worklist.groups || []).map((group, index) => (
          <details key={group.blocker} open={index < 4} className="overflow-hidden rounded-md border border-stone-200">
            <summary className="cursor-pointer bg-white px-3 py-2 text-sm font-medium text-stone-800">
              <div className="flex flex-wrap items-center gap-3">
                <span>{label(group.blocker)} · {group.count}</span>
                <CountChips counts={group.priority_gap_counts} />
              </div>
            </summary>
            <div className="overflow-x-auto border-t border-stone-100">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-stone-50 text-[11px] font-medium uppercase tracking-wide text-stone-400">
                  <tr>
                    <th className="px-3 py-2">Card</th>
                    <th className="px-3 py-2">Gap</th>
                    <th className="px-3 py-2">Blocker</th>
                    <th className="px-3 py-2">Current type</th>
                    <th className="px-3 py-2">Source / Enrichment</th>
                    <th className="px-3 py-2">Review / Publish</th>
                    <th className="px-3 py-2">Coordinates</th>
                  </tr>
                </thead>
                <tbody>{(group.cards || []).map(row => <PublishingRow key={`${row.place_master_id}:${row.card_key}`} row={row} />)}</tbody>
              </table>
            </div>
            <p className="break-words border-t border-stone-100 bg-stone-50 p-2 font-mono text-xs text-stone-600">{group.place_master_ids_text}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function IntentSignalCoverage({ coverage }) {
  if (!coverage) return null;
  return (
    <section className="rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-stone-900">Intent Signal Coverage</h2>
          <p className="text-xs text-stone-500">
            Published recommendation cards · {coverage.published_recommendation_count || 0} total · {coverage.cards_without_governed_intent || 0} without a governed trip intent
          </p>
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="text-[11px] font-medium uppercase tracking-wide text-stone-400">
            <tr>
              <th className="px-3 py-2">Intent</th>
              <th className="px-3 py-2">Count</th>
              <th className="px-3 py-2">Sample cards</th>
            </tr>
          </thead>
          <tbody>
            {(coverage.values || []).map(item => (
              <tr key={item.value} className="border-t border-stone-100 align-top">
                <td className="px-3 py-2"><Badge value={item.value} /></td>
                <td className="px-3 py-2 font-medium text-stone-900">{item.count || 0}</td>
                <td className="px-3 py-2 text-xs text-stone-600">
                  {(item.samples || []).map(sample => sample.card_title).join(', ') || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BucketTable({ bucket, rows }) {
  if (!rows?.length) return null;
  return (
    <section className="overflow-hidden rounded-lg border border-stone-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 bg-stone-50 px-4 py-3">
        <div>
          <h2 className="font-semibold text-stone-900">{BUCKET_LABELS[bucket] || label(bucket)}</h2>
          <p className="text-xs text-stone-500">{rows.length} card{rows.length === 1 ? '' : 's'} · closest to ready first</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-white text-[11px] font-medium uppercase tracking-wide text-stone-400">
            <tr>
              <th className="px-3 py-2">Card</th>
              <th className="px-3 py-2">Current type</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Enrichment</th>
              <th className="px-3 py-2">Publish</th>
              <th className="px-3 py-2">Coordinates</th>
              <th className="px-3 py-2">Composer</th>
            </tr>
          </thead>
          <tbody>{rows.map(row => <Row key={`${row.place_master_id}:${row.card_key}`} row={row} />)}</tbody>
        </table>
      </div>
    </section>
  );
}

export default function LaunchTriageView() {
  const [areaInput, setAreaInput] = useState('Bracebridge');
  const [area, setArea] = useState('Bracebridge');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = async () => {
    setLoading(true);
    setError('');
    try {
      const next = await api.getLaunchBucketTriage({ area });
      setReport(next);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReport(); }, [area]); // eslint-disable-line react-hooks/exhaustive-deps

  const bucketEntries = useMemo(() => {
    const buckets = report?.buckets || {};
    return Object.keys(BUCKET_LABELS).map(key => [key, buckets[key] || []]);
  }, [report]);

  const summary = report?.summary || {};
  const biggestBlocker = (report?.blocker_groups || []).filter(group => group.blocker !== 'composer_ready')
    .sort((a, b) => b.count - a.count)[0];

  return (
    <div className="space-y-4 text-stone-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Launch Triage</h1>
          <p className="text-sm text-stone-500">Read-only launch buckets for reviewing guest-relevant cards by area.</p>
        </div>
        <form
          onSubmit={event => {
            event.preventDefault();
            setArea(areaInput.trim() || 'Bracebridge');
          }}
          className="flex items-center gap-2"
        >
          <div className="flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2">
            <Search className="h-4 w-4 text-stone-400" />
            <input
              value={areaInput}
              onChange={event => setAreaInput(event.target.value)}
              className="w-44 text-sm outline-none"
              placeholder="Area"
            />
          </div>
          <button type="submit" className="rounded-lg bg-stone-900 px-3 py-2 text-sm font-medium text-white hover:bg-stone-800">Run</button>
          <button
            type="button"
            onClick={loadReport}
            className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </form>
      </div>

      {error && <ErrorAlert error={error} onClose={() => setError('')} />}
      {loading && <p className="rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm text-stone-500">Loading launch triage...</p>}

      {report && (
        <>
          <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
            <SummaryCard title="Active" value={summary.total_active ?? 0} sub={`${summary.local_only_cut ?? 0} cut as local-only`} />
            <SummaryCard title="Composer-ready" value={summary.composer_ready_total ?? 0} sub="Published, trusted, sourced" />
            <SummaryCard title="Publishing Worklist" value={summary.publishing_worklist_count ?? 0} sub="Guest-relevant, not published" />
            <SummaryCard title="Unmapped" value={summary.unmapped_count ?? 0} sub="Needs bucket decision" />
            <SummaryCard title="Biggest blocker" value={biggestBlocker ? label(biggestBlocker.blocker) : '-'} sub={biggestBlocker ? `${biggestBlocker.count} cards` : ''} />
            <SummaryCard title="Stale Published" value={summary.published_projection_stale_count ?? 0} sub="Published item, draft working card" />
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <SummaryCard title="Rental Providers" value={summary.rental_provider_count ?? 0} sub={`${summary.gear_access_support_count ?? 0} access-support cards`} />
            <SummaryCard title="Multi-Offering" value={summary.multi_offering_candidate_count ?? 0} sub="Flagged for later split review" />
            <SummaryCard title="Duplicate Pairs" value={summary.duplicate_candidate_count ?? 0} sub="Review list only" />
          </div>

          <PublishingWorklist worklist={report.publishing_worklist} />
          <IntentSignalCoverage coverage={report.intent_signal_coverage} />

          <section className="rounded-lg border border-stone-200 bg-white p-4">
            <h2 className="font-semibold text-stone-900">Bucket Summary</h2>
            <div className="mt-3 grid gap-2 md:grid-cols-3 lg:grid-cols-6">
              {bucketEntries.map(([bucket, rows]) => (
                <div key={bucket} className="rounded-md border border-stone-200 px-3 py-2">
                  <p className="text-xs font-medium text-stone-500">{BUCKET_LABELS[bucket]}</p>
                  <p className="text-lg font-semibold text-stone-900">{rows.length}</p>
                  <p className="text-xs text-stone-500">{summary.composer_ready_by_bucket?.[bucket] || 0} ready</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-stone-200 bg-white p-4">
            <h2 className="font-semibold text-stone-900">Batch Blockers</h2>
            <div className="mt-3 space-y-2">
              {(report.blocker_groups || []).map(group => (
                <details key={group.blocker} className="rounded-md border border-stone-200 px-3 py-2">
                  <summary className="cursor-pointer text-sm font-medium text-stone-800">
                    {label(group.blocker)} · {group.count}
                  </summary>
                  <p className="mt-2 break-words rounded bg-stone-50 p-2 font-mono text-xs text-stone-600">{group.place_master_ids_text}</p>
                </details>
              ))}
            </div>
          </section>

          <DuplicateReview pairs={report.duplicate_candidates || []} />

          <div className="space-y-4">
            {bucketEntries.map(([bucket, rows]) => <BucketTable key={bucket} bucket={bucket} rows={rows} />)}
          </div>

          {Boolean(report.local_only?.length) && (
            <BucketTable bucket="local_only" rows={report.local_only} />
          )}
        </>
      )}
    </div>
  );
}
